import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { marked } from "marked";
import { streamChat, type ChatMessage } from "../lib/chatStream";
import ActionCardPanel from "./ActionCardPanel";
import type { ActionCard } from "../types/cards";

// Three shapes Colleen actually uses the chat for: solving the gap,
// checking conflicts, and an ambient temperature check.
const SUGGESTIONS = [
  "Find fill-ins for the open sub-requests",
  "Who's away this weekend?",
  "Anyone I should check in on?",
];

interface Props {
  // Card state is owned by Dashboard (one useActionCards) so the same
  // cards drive both this panel and the Needs-Your-Attention merge.
  cards: ActionCard[];
  cardsLoading: boolean;
  onApply: (cardId: string, lineIds: string[]) => Promise<unknown>;
  onRetry: (cardId: string, lineId: string) => Promise<unknown>;
  onDismiss: (cardId: string) => Promise<unknown>;
  onCardStreamed: (card: ActionCard, replacedCardId: string | null) => void;
  // A pending card clicked in Needs Your Attention — expand the panel
  // and scroll/highlight it. onClearFocus resets it (used by collapse).
  focusCardId: string | null;
  onClearFocus: () => void;
  // Reports whether the panel is expanded so Dashboard can keep a
  // pending card in exactly one place: the open panel, or — when
  // collapsed — Needs Your Attention.
  onExpandedChange: (expanded: boolean) => void;
}

// Dashboard-embedded chat (v1.1 — no /chat route). Compact, it's just
// the input + suggestion chips. Sending a message expands the panel IN
// PLACE: the conversation thread grows above the input, and the action
// cards render alongside (right on desktop, below on mobile). This
// Week and Needs Your Attention stay visible above; the dashboard is
// the workspace, the Ask panel is just taller while active. A quiet
// "Collapse" shrinks back to the single input — pending cards remain
// in Needs Your Attention regardless.
export default function ChatPanel({
  cards,
  cardsLoading,
  onApply,
  onRetry,
  onDismiss,
  onCardStreamed,
  focusCardId,
  onClearFocus,
  onExpandedChange,
}: Props) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cardError, setCardError] = useState(false);
  // True between card_pending (sentinel reached) and the real card —
  // drives the placeholder skeleton in the card column.
  const [preparingCard, setPreparingCard] = useState(false);
  // Set when the last ACTIVE card is dismissed → the panel collapses
  // back to the single input even though receipts may remain (they
  // re-render when the panel reopens). Reset on send / focus / reload.
  const [collapsed, setCollapsed] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const scrollAnchorRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  // Serializes card dismissal so one click-burst removes exactly one
  // card (see handleCardDismiss).
  const dismissLockRef = useRef(false);
  const dismissUnlockTimer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (dismissUnlockTimer.current !== null) {
        window.clearTimeout(dismissUnlockTimer.current);
      }
    },
    [],
  );

  // Groups this session's cards server-side so a chat refinement can
  // replace the pending card it supersedes.
  const conversationId = useMemo(() => crypto.randomUUID(), []);

  // Expanded whenever there's something to show in the panel: an active
  // conversation, a focused card, a card being prepared, or applied
  // RECEIPTS (so the Earlier history re-renders on reload — v1.3).
  //
  // Note receipts (not all cards) drive auto-expansion. A lone PENDING
  // card does NOT force the panel open: a card you created but walked
  // away from surfaces in Needs Your Attention instead, and the panel
  // stays compact until you reopen it (send / click the attention
  // card). That's what keeps a pending card in exactly one place. The
  // dismiss cascade (last active card removed) overrides this until
  // reopened.
  const hasReceipts = cards.some((c) => c.status === "applied");
  const expanded =
    !collapsed &&
    (messages.length > 0 || focusCardId !== null || preparingCard || hasReceipts);

  // The action-card column only exists once there's a card to show.
  // Write actions are disabled today, so there never is — the chat
  // renders as a single full-width column instead of reserving an
  // empty 60% rail. When write actions ship and cards start arriving,
  // this flips true and the two-column split returns automatically.
  const hasCardColumn = cards.length > 0 || preparingCard || cardError;

  // Report expansion up so Dashboard can drop the pending card from
  // Needs Your Attention while the panel shows it.
  useEffect(() => {
    onExpandedChange(expanded);
  }, [expanded, onExpandedChange]);

  // Auto-scroll the thread as tokens arrive.
  useEffect(() => {
    scrollAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages]);

  // When a card is focused from Needs Your Attention, reopen the panel
  // (clear an earlier close) and bring it into view (ActionCardPanel
  // scrolls to the specific card).
  useEffect(() => {
    if (focusCardId) {
      setCollapsed(false);
      panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [focusCardId]);

  async function send(text: string) {
    const content = text.trim();
    if (!content || streaming) return;

    const next: ChatMessage[] = [...messages, { role: "user", content }];
    setMessages([...next, { role: "assistant", content: "" }]);
    setInput("");
    setError(null);
    setCardError(false);
    setCollapsed(false); // sending reopens the panel if it was collapsed
    setStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;
    try {
      await streamChat({
        messages: next,
        conversationId,
        signal: controller.signal,
        onDelta: (delta) => {
          setMessages((prev) => {
            const copy = [...prev];
            const last = copy[copy.length - 1];
            if (!last || last.role !== "assistant") return prev;
            copy[copy.length - 1] = { ...last, content: last.content + delta };
            return copy;
          });
        },
        onCardPending: () => setPreparingCard(true),
        onCard: (card, replacedCardId) => {
          onCardStreamed(card, replacedCardId);
          setPreparingCard(false);
        },
        onCardError: () => {
          setCardError(true);
          setPreparingCard(false);
        },
      });
    } catch (err) {
      if ((err as Error).name === "AbortError") return;
      setError((err as Error).message);
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last && last.role === "assistant" && last.content === "") {
          return prev.slice(0, -1);
        }
        return prev;
      });
    } finally {
      setStreaming(false);
      setPreparingCard(false); // clear any lingering placeholder
      abortRef.current = null;
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    void send(input);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void send(input);
    }
  }

  // Dismiss a card from the panel (its top-right ×). When it was the
  // LAST active (pending) card, the panel collapses back to the single
  // input — the dismiss cascade. Receipts don't count as active and
  // don't block the collapse; they re-render when the panel reopens.
  async function handleCardDismiss(cardId: string) {
    // One dismiss per click-burst. As each card is removed the next
    // shifts UP into the cursor's spot, so a flurry of clicks at the
    // same ✕ position (e.g. impatient re-clicks while the request is in
    // flight) would otherwise cascade-dismiss every card. The lock holds
    // through the request and a short settle window after, so re-clicks
    // land on a locked panel, not the next card's ×.
    if (dismissLockRef.current) return;
    dismissLockRef.current = true;
    const activeBefore = cards.filter((c) => c.status === "pending").length;
    try {
      await onDismiss(cardId);
      if (activeBefore <= 1) {
        abortRef.current?.abort();
        setMessages([]);
        setInput("");
        setError(null);
        setCardError(false);
        setPreparingCard(false);
        setCollapsed(true);
        onClearFocus();
      }
    } finally {
      if (dismissUnlockTimer.current !== null) {
        window.clearTimeout(dismissUnlockTimer.current);
      }
      dismissUnlockTimer.current = window.setTimeout(() => {
        dismissLockRef.current = false;
        dismissUnlockTimer.current = null;
      }, 500);
    }
  }

  return (
    <div ref={panelRef} className="dashboard__section dashboard__section--chat">
      <h2 id="ask-title" className="dashboard__section-label">
        Ask Sacristy AI
      </h2>
      <section
        className={`chat-panel${expanded ? " chat-panel--expanded" : ""}`}
        aria-labelledby="ask-title"
      >
        {expanded ? (
        <div
          className={`chat-panel__workspace${
            hasCardColumn ? "" : " chat-panel__workspace--solo"
          }`}
        >
          <div className="chat-panel__thread">
            <div className="chat-panel__transcript" role="log" aria-live="polite">
              {messages.length === 0 ? (
                <p className="chat-panel__focus-note">
                  Describe a change, or ask about the schedule.
                </p>
              ) : null}
              {messages.map((m, i) => (
                <MessageBubble
                  key={i}
                  role={m.role}
                  content={m.content}
                  streaming={
                    streaming && i === messages.length - 1 && m.role === "assistant"
                  }
                />
              ))}
              <div ref={scrollAnchorRef} />
            </div>

            {error ? <p className="chat-panel__error">{error}</p> : null}

            <form className="chat-panel__form" onSubmit={onSubmit}>
              <textarea
                ref={inputRef}
                className="chat-panel__input"
                placeholder="Reply, or a new change…"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                rows={1}
                aria-label="Message"
                disabled={streaming}
              />
              {streaming ? (
                <button
                  type="button"
                  className="chat-panel__stop"
                  onClick={() => abortRef.current?.abort()}
                >
                  Stop
                </button>
              ) : (
                <button
                  type="submit"
                  className="chat-panel__send"
                  disabled={!input.trim()}
                >
                  Send
                </button>
              )}
            </form>
          </div>

          {hasCardColumn ? (
            <ActionCardPanel
              cards={cards}
              loading={cardsLoading}
              cardError={cardError}
              preparing={preparingCard}
              focusCardId={focusCardId}
              onApply={onApply}
              onRetry={onRetry}
              onDismiss={handleCardDismiss}
            />
          ) : null}
        </div>
      ) : (
        <>
          <ul className="chat-panel__suggestions" aria-label="Suggested prompts">
            {SUGGESTIONS.map((s) => (
              <li key={s}>
                <button
                  type="button"
                  className="chat-panel__suggestion"
                  onClick={() => void send(s)}
                >
                  {s}
                </button>
              </li>
            ))}
          </ul>

          <form className="chat-panel__form" onSubmit={onSubmit}>
            <textarea
              ref={inputRef}
              className="chat-panel__input"
              placeholder="Ask about volunteers, Masses, or open sub-requests…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              rows={1}
              aria-label="Message"
            />
            <button type="submit" className="chat-panel__send" disabled={!input.trim()}>
              Send
            </button>
          </form>
        </>
      )}
      </section>
    </div>
  );
}

interface MessageBubbleProps {
  role: ChatMessage["role"];
  content: string;
  streaming: boolean;
}

function MessageBubble({ role, content, streaming }: MessageBubbleProps) {
  const empty = content.length === 0;
  // Assistant messages come back as markdown — render them as HTML so
  // **bold**, lists, and headings format properly. User messages stay
  // as plain text since the user typed them.
  const html = useMemo(() => {
    if (role !== "assistant" || empty) return "";
    return marked.parse(content, { async: false, gfm: true, breaks: true }) as string;
  }, [role, content, empty]);
  return (
    <div className={`chat-msg chat-msg--${role}`}>
      <div className="chat-msg__bubble">
        {empty && streaming ? (
          <span className="chat-msg__thinking" aria-label="Thinking">
            <span />
            <span />
            <span />
          </span>
        ) : role === "assistant" ? (
          <div
            className="chat-msg__rich"
            // Assistant output only — content originates from our worker
            // calling Sonnet 4.6, not from arbitrary user input.
            dangerouslySetInnerHTML={{ __html: html }}
          />
        ) : (
          <span className="chat-msg__text">{content}</span>
        )}
      </div>
    </div>
  );
}
